import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";

import Navigation from "./components/Navigation";
import Footer from "./components/Footer";
import CustomCursor from "./components/CustomCursor";
import IntroAnimation from "./components/IntroAnimation";
import { Toaster } from "./components/ui/sonner";

import Home from "./pages/Home";
import About from "./pages/About";
import Services from "./pages/Services";
import Journal from "./pages/Journal";
import JournalPost from "./pages/JournalPost";
import Apply from "./pages/Apply";
import Studio from "./pages/Studio";
import StudioItem from "./pages/StudioItem";
import StudioProduct from "./pages/StudioProduct";
import StudioSuccess from "./pages/StudioSuccess";
import StudioCoachingApply from "./pages/StudioCoachingApply";
import Giveback from "./pages/Giveback";
import MyLibrary from "./pages/MyLibrary";
import LibraryReader from "./pages/LibraryReader";
import LibraryLogin from "./pages/LibraryLogin";
import Careers from "./pages/Careers";
import MScore from "./pages/MScore";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";

import { PortalAuthProvider, ProtectedPortalRoute, ProtectedLibraryRoute } from "./portal/PortalAuth";
import PortalLogin from "./portal/PortalLogin";
import PortalDashboard from "./portal/PortalDashboard";
import Analytics from "./components/Analytics";
import SeoManager from "./components/SeoManager";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [pathname]);
  return null;
}

/**
 * Global "nudge" — after every route change AND when the tab regains focus,
 * find every background <video> on the page and call .play(). Catches the
 * cases where Safari low-power mode, iOS battery saver, or React's hydration
 * skip the autoplay attribute.
 */
function VideoAutoplayWatcher() {
  const { pathname } = useLocation();
  useEffect(() => {
    const ensurePlaying = () => {
      document.querySelectorAll("video").forEach((v) => {
        // Skip the intro modal video — IntroAnimation manages itself
        if (v.closest(".intro-video, .intro-overlay")) return;
        // Skip videos that intentionally have controls (i.e. user-driven)
        if (v.controls) return;
        v.muted = true;
        if (v.paused) {
          const p = v.play();
          if (p && typeof p.catch === "function") p.catch(() => {});
        }
      });
    };
    // Initial + after route change (small delay so React commits videos)
    const t1 = setTimeout(ensurePlaying, 80);
    const t2 = setTimeout(ensurePlaying, 600);
    const onVisible = () => { if (!document.hidden) ensurePlaying(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", ensurePlaying);
    return () => {
      clearTimeout(t1); clearTimeout(t2);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", ensurePlaying);
    };
  }, [pathname]);
  return null;
}

function PublicLayout({ children }) {
  return (
    <>
      <CustomCursor />
      <Navigation />
      <main>{children}</main>
      <Footer />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <IntroAnimation />
      <Toaster richColors position="top-right" />
      <BrowserRouter>
        <ScrollToTop />
        <VideoAutoplayWatcher />
        <Analytics />
        <SeoManager />
        <PortalAuthProvider>
        <Routes>
          <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
          <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
          <Route path="/services" element={<PublicLayout><Services /></PublicLayout>} />
          <Route path="/studio" element={<PublicLayout><Studio /></PublicLayout>} />
          <Route path="/studio/success" element={<PublicLayout><StudioSuccess /></PublicLayout>} />
          <Route path="/studio/coaching/apply" element={<PublicLayout><StudioCoachingApply /></PublicLayout>} />
          <Route path="/studio/:id" element={<PublicLayout><StudioProduct /></PublicLayout>} />
          <Route path="/give" element={<PublicLayout><Giveback /></PublicLayout>} />
          <Route path="/giveback" element={<Navigate to="/give" replace />} />
          <Route path="/journal" element={<PublicLayout><Journal /></PublicLayout>} />
          <Route path="/journal/:slug" element={<PublicLayout><JournalPost /></PublicLayout>} />
          <Route path="/apply" element={<PublicLayout><Apply /></PublicLayout>} />
          <Route path="/matter" element={<PublicLayout><MScore /></PublicLayout>} />
          <Route path="/score" element={<Navigate to="/matter" replace />} />
          <Route path="/contact" element={<PublicLayout><Apply /></PublicLayout>} />
          {/* Portfolio retired for client confidentiality */}
          <Route path="/work" element={<Navigate to="/" replace />} />
          <Route path="/work/:slug" element={<Navigate to="/" replace />} />
          <Route path="/team" element={<Navigate to="/about" replace />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminDashboard />} />

          {/* Client + Team Portal */}
          <Route path="/portal" element={<PortalLogin />} />
          <Route path="/portal/dashboard" element={
            <ProtectedPortalRoute><PortalDashboard /></ProtectedPortalRoute>
          } />

          {/* Studio — buyer-facing "shelf" experience.
              Separate door from /portal (which is for the team + retainer clients). */}
          <Route path="/library/login" element={<PublicLayout><LibraryLogin /></PublicLayout>} />
          <Route path="/library" element={
            <ProtectedLibraryRoute><PublicLayout><MyLibrary /></PublicLayout></ProtectedLibraryRoute>
          } />
          <Route path="/library/:slug" element={
            <ProtectedLibraryRoute><LibraryReader /></ProtectedLibraryRoute>
          } />

          {/* Careers — apply to work with M Media */}
          <Route path="/careers" element={<PublicLayout><Careers /></PublicLayout>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </PortalAuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
